// Helpers for the weekly plan agenda. Weeks are Monday-based, matching the
// dayNames order used across the diary code: Monday … Sunday.

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const pad = (n: number): string => String(n).padStart(2, '0');

// Format a Date as "DD-MM-YYYY" (the convention used by the diary endpoints).
export const formatDDMMYYYY = (d: Date): string =>
  `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;

// Monday of the week containing `base` (defaults to today).
const mondayOf = (base: Date): Date => {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const dow = d.getDay(); // 0=Sunday … 6=Saturday
  const diff = dow === 0 ? -6 : 1 - dow; // shift back to Monday
  d.setDate(d.getDate() + diff);
  return d;
};

// 7 Monday-based dates for the target week. weekOffset 0 = current week.
export const getWeekDays = (weekOffset: number): Date[] => {
  const start = mondayOf(new Date());
  start.setDate(start.getDate() + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

// `count` consecutive dates starting at (today + startOffsetDays).
export const getDaysFromToday = (startOffsetDays: number, count: number): Date[] => {
  const base = new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() + startOffsetDays);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// "DD-MM-YYYY" date part of a record's "DD-MM-YYYY HH:MM:SS" string.
export const recordDatePart = (date: string): string => (date || '').split(' ')[0];

// Parse a "DD-MM-YYYY" string into a Date (local midnight); null if malformed.
export const parseDDMMYYYY = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const parts = s.split(' ')[0].split('-');
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy) return null;
  const d = new Date(yyyy, mm - 1, dd);
  return isNaN(d.getTime()) ? null : d;
};
